import { FunctionComponent, useContext, useState } from 'react'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, Stack, TextField, Typography, useMediaQuery } from '@mui/material'
import { DatePicker, LocalizationProvider, } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment, { Moment } from 'moment'
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import gaiaTheme from '../../../../root-components/theme'
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons'

export const StartNewReportPeriodDialog: FunctionComponent<{
  open: boolean,
  closeHandler: (confirmed: boolean) => void,
  getAllReportPeriods?: () => void,
}> = ({
  open,
  closeHandler,
  getAllReportPeriods
}) => {
    //Services
    const reportPeriodService = new ReportPeriodService();
    const reportPeriodContext = useContext(ReportPeriodContext);
    //State
    const [openDate, setOpenDate] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>('')
    //Layout
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));

    //Formik
    const newReportPeriodSchema = Yup.object().shape({
      friendlyName: Yup.string().required('Name is required'),
      startDate: Yup.string().required('Start date is required').test('is-first-of-month', 'Start date must be the first of the month', (value) => {
        if (!value) return false;
        const date = moment(value);
        return date.isValid() && date.date() === 1;
      }),
      endDate: Yup.string().required('End date is required'),
    })

    const formik = useFormik({
      initialValues: {
        startDate: '',
        endDate: '',
        friendlyName: ''
      },
      validationSchema: newReportPeriodSchema,
      onSubmit: (values) => {
        if (!isSubmitting) {
          setIsSubmitting(true)
          reportPeriodService.createReportPeriod(values)
            .then(() => {
              reportPeriodContext.forceUpdateReportPeriods();
              if (getAllReportPeriods) {
                getAllReportPeriods()
              }
              closeHandler(true);
              setErrorMessage('')
              formik.resetForm();
              setIsSubmitting(false)
            })
            .catch(err => {
              setErrorMessage(err.response.data.message)
              setIsSubmitting(false)
            })
        }
      }
    });

    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        const updatedReportPeriod = { ...formik.values, [name]: date.format('YYYY-MM-DD') };
        if (name === 'startDate') {
          updatedReportPeriod.endDate = moment(date).add(1, 'year').subtract(1, 'day').format('YYYY-MM-DD');
          updatedReportPeriod.friendlyName = moment(updatedReportPeriod.endDate).year().toString();
        }
        formik.setValues(updatedReportPeriod);
      }
    }

    const cancelHandler = () => {
      formik.resetForm()
      closeHandler(false);
    }

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
        <Box sx={{ p: 4 }} component='form' onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
            Start New Report Period
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <Typography variant='body1'>The report period usually aligns with your financial year but can also follow the calendar year.</Typography>
            {/* <TextField
              id='friendlyName'
              name='friendlyName'
              label='Name'
              fullWidth value={formik.values.friendlyName} onChange={formik.handleChange} sx={{ mt: 2 }}
              error={formik.touched.friendlyName && Boolean(formik.errors.friendlyName)}
              helperText={formik.touched.friendlyName && formik.errors.friendlyName}
            /> */}
          </DialogContent>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <FormControl fullWidth variant='outlined'>
              <Stack sx={{ borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main', pt: 1, pb: 1, pl: isMobileMode ? 0 : 3 }}>
                <Stack position='relative' flexDirection={isMobileMode ? 'column' : 'row'} justifyContent='start' alignItems={isMobileMode ? 'flex-start' : 'center'}>
                  <Typography variant='subtitle1' sx={{ width: '30%', minWidth: '100px', color: 'text.primary' }}> Start Date</Typography>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      value={formik.values.startDate ? moment(formik.values.startDate) : null}
                      onChange={(date) => dateHandler(date, 'startDate')}
                      format='DD/MM/YYYY'
                      shouldDisableDate={(date) => date.date() !== 1}
                      open={openDate}
                      sx={{ width: '100%' }}
                      onOpen={() => setOpenDate(true)}
                      onClose={() => setOpenDate(false)}
                      slotProps={{
                        textField: {
                          onClick: () => setOpenDate(true),
                          error: formik.touched.startDate && Boolean(formik.errors.startDate)
                        },
                      }}
                    />
                    {formik.touched.startDate && formik.errors.startDate &&
                      <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.startDate}</FormHelperText>
                    }

                  </LocalizationProvider>
                </Stack>
              </Stack>
            </FormControl>
            <FormControl fullWidth variant='outlined'>
              <Stack position='relative' flexDirection={isMobileMode ? 'column' : 'row'} justifyContent='start' alignItems={isMobileMode ? 'flex-start' : 'center'} sx={{ pt: 1, pb: 1, pl: isMobileMode ? 0 : 3 }}>
                <Typography variant='subtitle1' sx={{ width: '30%', minWidth: '100px', color: 'text.primary' }}>End Date</Typography>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    disabled
                    sx={{ width: '100%' }}
                    value={formik.values.endDate ? moment(formik.values.endDate) : null}
                    onChange={(date) => dateHandler(date, 'endDate')}
                    format='DD/MM/YYYY'
                  />
                </LocalizationProvider>
              </Stack>
            </FormControl>
            {errorMessage &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}
              </FormHelperText>
            }
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <DialogSaveButtons
              cancelHandler={cancelHandler}
              submitHandler={formik.submitForm}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Box>

      </Dialog >
    )
  }